import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="31395" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[14px] font-medium uppercase tracking-widest text-foreground transition-all border-b-2 border-transparent hover:text-primary py-1">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium py-3 px-4 rounded-md hover:bg-secondary hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="navigation" className={`sticky top-0 z-50 w-full border-b transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-transparent shadow-md/5" : "bg-background border-transparent shadow-none"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581972/img/maison_jas_logo.png" alt="Maison Jas Logo" className="h-7 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-4">
          <Badge variant="secondary" className="hidden sm:flex font-sans text-[10px] uppercase tracking-widest bg-secondary text-primary border-primary/10 px-3 py-1 rounded-full">
            Heilung & Wachstum
          </Badge>
        </div>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="16" className="flex items-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="Menü öffnen" className="text-foreground">
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-md md:hidden animate-in fade-in slide-in-from-top-5 duration-300">
          <div className="container mx-auto px-4 py-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-8 h-12">
              <div className="flex items-center gap-3">
                 <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581972/img/maison_jas_logo.png" alt="Maison Jas Logo" className="h-7 w-auto object-contain" />
                <Badge variant="outline" className="font-sans text-[10px] uppercase tracking-widest text-primary border-primary/20 rounded-full">
                    Zürich
                </Badge>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Menü schließen">
                <X className="h-6 w-6" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="16">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>

            <div className="py-8 border-t border-border/50 mt-auto">
               <p className="text-sm text-muted-foreground text-center mb-6 font-serif italic">
                 Ganzheitliche Begleitung für Ihre persönliche Transformation.
               </p>
               <div className="flex justify-center">
                 <Button asChild className="w-full max-w-xs bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-md">
                   <Link to="#">
                     <Phone className="mr-2 h-4 w-4" /> Termin vereinbaren
                   </Link>
                 </Button>
               </div>
            </div>
          </div>
        </div>}
    </header>;
}